import React from 'react';

export const safeString = (v) => (v ?? '').toString();

export const isHandlebarsToken = (s) => {
  const token = safeString(s).trim();
  return token.startsWith('{{') && token.endsWith('}}');
};

export const extractHandlebarsTokens = (text) => {
  const matches = safeString(text).match(/\{\{[^}]+\}\}/g) ?? [];
  return Array.from(new Set(matches.map(x => safeString(x).trim()).filter(Boolean)));
};

const walkTemplateItems = (items, out) => {
  const list = Array.isArray(items) ? items : [];
  for (const item of list) {
    const token = safeString(item?.token).trim();
    if (token) out.push(token);
    const children = Array.isArray(item?.items) ? item.items : [];
    if (children.length > 0) walkTemplateItems(children, out);
  }
};

/** True for ready-made JS context paths like "ctx.query" or "ctx.vars.jobID". */
export const isContextPathToken = (s) => (
  /^ctx(\.[A-Za-z_$][\w$]*)+$/.test(safeString(s).trim())
);

/**
 * Flattens a template catalog into its unique tokens, sorted. Accepts handlebars tokens
 * by default; pass a different predicate for catalogs that also carry ready-made ctx.*
 * snippet tokens (e.g. custom validation's JS catalog).
 */
export const collectCatalogTokens = (templateCatalog, isAllowedToken = isHandlebarsToken) => {
  const tokens = [];
  for (const group of (Array.isArray(templateCatalog) ? templateCatalog : [])) {
    walkTemplateItems(group?.items, tokens);
  }
  return Array.from(new Set(tokens.filter(isAllowedToken))).sort();
};

const makeId = () => `${Date.now()}_${Math.random().toString(16).slice(2)}`;

/**
 * Shared state for the AI chat assistants: the conversation transcript and the
 * version history of the artifact (query/script) being worked on.
 *
 * Messages may carry a `version` field ({ id, seq, testOk }) linking them to an
 * artifact version, which the dialog renders as a restorable chip.
 */
export const useAiChatAssistant = ({ initialArtifact } = {}) => {
  const [chatMessages, setChatMessages] = React.useState([]);
  const [versions, setVersions] = React.useState(() => {
    const initial = safeString(initialArtifact);
    return initial
      ? [{ id: makeId(), seq: 1, content: initial, ts: new Date().toISOString(), source: 'initial' }]
      : [];
  });

  const versionsRef = React.useRef(versions);
  versionsRef.current = versions;

  const pushMsg = React.useCallback((role, text, extra) => {
    const trimmed = safeString(text).trim();
    if (!trimmed) return;
    const msg = {
      id: makeId(),
      role: role || 'system',
      text: trimmed,
      ts: new Date().toISOString(),
      ...(extra ?? {}),
    };
    // The transcript is NEVER capped: a cap here once let system-message noise (restore
    // clicks, test results) silently evict the real user/assistant/tool history — which
    // buildServerMessages also feeds the model, so the AI lost its context along with
    // the display. Token growth is a server concern, handled at prompt-build time.
    setChatMessages(prev => [...(Array.isArray(prev) ? prev : []), msg]);
  }, []);

  /** Appends a version unless the content matches the latest one. Returns the version entry. */
  const pushVersion = React.useCallback((content, source) => {
    const text = safeString(content);
    if (!text) return null;
    const latest = versionsRef.current[versionsRef.current.length - 1];
    if (latest && latest.content === text) return latest;
    const entry = {
      id: makeId(),
      seq: (latest?.seq ?? 0) + 1,
      content: text,
      ts: new Date().toISOString(),
      source: source || 'ai',
    };
    versionsRef.current = [...versionsRef.current, entry];
    setVersions(versionsRef.current);
    return entry;
  }, []);

  const getVersion = React.useCallback(
    (id) => versionsRef.current.find(v => v.id === id) ?? null,
    []
  );

  /**
   * Maps the role-aware history from AccentPromptChat into the server transcript shape
   * ([{ role, content }]), keeping user/assistant turns plus hidden "tool" records (the
   * server's oracle round records, which later turns build on), and excluding the message
   * currently being sent (the server receives that separately as the prompt).
   */
  const buildServerMessages = React.useCallback((history, currentText) => {
    const list = Array.isArray(history) ? [...history] : [];
    const last = list[list.length - 1];
    if (last &&
      safeString(last.role).toLowerCase() === 'user' &&
      safeString(last.text).trim() === safeString(currentText).trim()) {
      list.pop();
    }
    // A user message immediately followed by an ERROR bubble is a FAILED turn (the
    // hosts push 'error' right after the user's message when the send throws — e.g.
    // AI usage limits). The model never saw that message, so replaying it as history
    // duplicates it when the user retries the same text. Success paths never produce
    // this adjacency: their notices land after the assistant reply.
    const failedTurn = (m, i) =>
      safeString(m?.role).toLowerCase() === 'user' &&
      safeString(list[i + 1]?.role).toLowerCase() === 'error';
    return list
      .filter((m, i) => !failedTurn(m, i))
      .filter(m => ['user', 'assistant', 'tool'].includes(safeString(m?.role).toLowerCase()))
      // intent ('ask' | 'change' | '') marks user messages sent via an explicit intent
      // button; the server transcript renders it so follow-up turns keep each
      // message's question-vs-change reading. images (the data URLs pasted with a
      // user message) ride back too: the server attaches the most recent paste to
      // its model calls as image input, so a design the user pasted two turns ago is
      // still what the model looks at when refining it.
      .map(m => ({
        role: safeString(m.role).toLowerCase(),
        content: safeString(m.text),
        intent: safeString(m.intent),
        ...(Array.isArray(m.images) && m.images.length > 0 ? { images: m.images } : {}),
      }))
      .filter(m => m.content.trim() !== '');
  }, []);

  return {
    chatMessages,
    setChatMessages,
    pushMsg,
    versions,
    pushVersion,
    getVersion,
    buildServerMessages,
  };
};
